import { requireAuth } from '@/auth/requireAuth'
import { PageTitle } from '@/layout/page-title'
import { createFileRoute, useLoaderData } from '@tanstack/react-router'
import type { Company } from "@/company"
import { MonthSelector } from '@/components/report/month-selector'
import { YearSelector } from '@/components/report/year-selector'
import { CompanySelector } from '@/components/company/company-selector'
import { SelectStatus } from '@/components/report/select-status'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import type { ActivityReportRequest } from '@/report'
import toast from 'react-hot-toast'
import { authFetch } from '@/api'
import { LoadingSpinner } from '@/components/ui/spinner'
import { StringSelect } from '@/components/global-summary/select-string'
import { getClosingDate } from '@/utils/closing-date'

export const Route = createFileRoute('/(app)/_layout/report')({
  component: RouteComponent,
  beforeLoad: async () => await requireAuth(),

})

function RouteComponent() {
  const closingDate = getClosingDate();
  const [reportRequest, setReportRequest] = useState<ActivityReportRequest>({
    month: closingDate.month, // Default to current month
    year: closingDate.year, // Default to current year
  })
  const [loading, setLoading] = useState(false);

  const { companiesData } = useLoaderData({ from: '/(app)/_layout' })
  const companies: Company[] = companiesData.data ?? [];

  const regions = [...new Set(companies.map((company: Company) => company.region))].sort((a, b) => a.localeCompare(b));
  const perimeters = [...new Set(companies.map((company: Company) => company.perimeter))].sort((a, b) => a.localeCompare(b));
  const countries = [...new Set(companies.map((company: Company) => company.country))].sort((a, b) => a.localeCompare(b));
  const businesses = [...new Set(companies.map((company: Company) => company.business))].sort((a, b) => a.localeCompare(b));
  const regionalCFOs = [...new Set(companies.map((company: Company) => company.regionalCFO).filter((cfo): cfo is string => cfo !== null && cfo !== undefined))].sort((a: string, b: string) => a.localeCompare(b));
  const countryFinanceManagers = [...new Set(companies.map((company: Company) => company.countryFinanceManager).filter((manager): manager is string => manager !== null && manager !== undefined))].sort((a: string, b: string) => a.localeCompare(b));


  const handleGenerateReport = async () => {
    try {
      setLoading(true)
      const filteredParams = Object.fromEntries(
        Object.entries(reportRequest).filter(([_, v]) => v !== undefined)
      );
      const queryParams = new URLSearchParams(filteredParams as any).toString();
      const url = `${import.meta.env.VITE_API_URL}/report/download?${queryParams}`;
      const response = await authFetch(url)
      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.message || "Failed to generate report");
      }
      const json = await response.blob()

      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([json], { type: 'application/xlsx' }));
      a.setAttribute('download', `report-${reportRequest.month}-${reportRequest.year}.xlsx`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Report generated successfully")
    } catch (error) {
      toast.error("Error generating report")
    } finally { setLoading(false) }
  }

  const onChangeRequest = (key: string) => (value: any) => {
    setReportRequest((prev) => ({
      ...prev,
      [key]: value
    }))
  }
  return (
    <div className="container max-w-[1900px] py-10 px-4">
      <div className="space-y-4">
        <PageTitle title="Report"
        />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-2">
              <MonthSelector defaultValue={reportRequest.month!!} onValueChange={onChangeRequest('month')} />
              <YearSelector defaultValue={reportRequest.year!!} onValueChange={onChangeRequest('year')} />
            </div>

            <div className="max-w-[350px]">

              <CompanySelector
                companies={companies}
                onSelectCompany={(company: Company | null) => {
                  onChangeRequest('companyId')(company?.id)
                }}
              />
            </div>

            <SelectStatus onValueChange={onChangeRequest('status')} />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StringSelect entityName='Region' items={regions} onSelectItem={onChangeRequest('region')} />
              <StringSelect entityName='Perimeter' items={perimeters} onSelectItem={onChangeRequest('perimeter')} />
              <StringSelect entityName='Country' items={countries} onSelectItem={onChangeRequest('country')} />
              <StringSelect entityName='Business' items={businesses} onSelectItem={onChangeRequest('business')} />
              <StringSelect entityName='Regional CFO' items={regionalCFOs} onSelectItem={onChangeRequest('regionalCFO')} />
              <StringSelect entityName='Country Finance Manager' items={countryFinanceManagers} onSelectItem={onChangeRequest('countryFinanceManager')} />
            </div>

            <Button disabled={loading} onClick={handleGenerateReport} className="w-[200px]">
              Generate Report
              {
                loading && <LoadingSpinner />
              }
            </Button>
          </div>
        </div>
      </div>
    </div >
  )
}
